import { PasswordInput, SelectInput, TextInput } from '@components/inputs'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAppDispatch } from '@hooks/reduxHooks'
import {
  alpha,
  Avatar,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import {
  CreateMemberDto,
  MemberDto,
  MemberRoleEnum,
  SexEnum,
  UpdateMemberDto,
} from '@services/api'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { removeMemberRoles } from '@state/reducers/roleReducer'
import { useSelector } from '@state/store'
import { createMember, updateMember } from '@state/thunks/memberThunk'
import { fetchMemberRoles, findAllRoles } from '@state/thunks/roleThunk'
import { addMemberSchema } from '@utils/schemas'
import { UserPlus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

type AddMemberFormProps = {
  onClose?: () => void
  row?: MemberDto
}

export function MemberForm({ row, onClose }: AddMemberFormProps) {
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const isEdit = !!row?.id

  const { sites, allRoles, memberRoles } = useSelector(({ site, role }) => ({
    sites: site.sites.datas,
    allRoles: role.roles,
    memberRoles: role.memberRoles,
  }))

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<MemberDto & { password: string }>({
    resolver: yupResolver(addMemberSchema),
    defaultValues: {
      role: MemberRoleEnum['health-executive'],
      phoneNumber: '',
      ...row,
      siteIds: row?.siteIds || [],
    },
    context: { isEdit },
  })

  const watchedSiteIds: number[] = watch('siteIds') || []

  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([])
  const [rolesInitialized, setRolesInitialized] = useState(false)

  useEffect(() => {
    dispatch(findAllRoles() as any)
  }, [dispatch])

  useEffect(() => {
    if (isEdit && row?.id) {
      dispatch(fetchMemberRoles(row.id) as any)
    }
    return () => {
      dispatch(removeMemberRoles())
    }
  }, [isEdit, row?.id, dispatch])

  useEffect(() => {
    if (!isEdit || rolesInitialized || !memberRoles) return

    const existingRoleIds = memberRoles.sites.flatMap((s) =>
      s.roles.map((r) => r.id),
    )
    if (existingRoleIds.length > 0) {
      setSelectedRoleIds(existingRoleIds)
      setRolesInitialized(true)
    }
  }, [isEdit, memberRoles, rolesInitialized])

  useEffect(() => {
    if (!rolesInitialized && isEdit) return
    setSelectedRoleIds((prev) =>
      prev.filter((id) =>
        allRoles.some((r) => r.id === id && watchedSiteIds.includes(r.siteId)),
      ),
    )
  }, [watchedSiteIds, allRoles, rolesInitialized, isEdit])

  const onSubmit = async (values: MemberDto & { password: string }) => {
    try {
      if (isEdit && row) {
        const updateMemberDto: UpdateMemberDto = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          address: values.address,
          phoneNumber: values.phoneNumber,
          role: values.role,
          password: values.password,
          siteIds: values.siteIds,
          roleIds: selectedRoleIds,
        }
        await dispatch(
          updateMember({ id: row.id as number, body: updateMemberDto }),
        ).unwrap()
        dispatch(
          enqueueSnackbar({
            type: 'success',
            message: 'Membre mis à jour avec succès',
          }),
        )
      } else {
        const createMemberDto: CreateMemberDto = {
          firstName: values.firstName,
          lastName: values.lastName,
          address: values.address,
          password: values.password,
          email: values.email,
          motherMaidenName: '',
          mobileNumber: '',
          phoneNumber: '',
          role: values.role,
          sex: SexEnum.A,
          siteIds: values.siteIds,
          roleIds: selectedRoleIds,
        }
        await dispatch(createMember(createMemberDto)).unwrap()
        dispatch(
          enqueueSnackbar({
            type: 'success',
            message: 'Membre créé avec succès',
          }),
        )
      }
      if (onClose) onClose()
    } catch {
      dispatch(
        enqueueSnackbar({ type: 'error', message: 'Une erreur est survenue' }),
      )
    }
  }

  const siteOptions = sites.map((_site) => ({
    label: _site.label,
    value: _site.id,
  }))

  const initials = isEdit
    ? `${row?.firstName?.[0] ?? ''}${row?.lastName?.[0] ?? ''}`
    : ''

  return (
    <>
      {/* Header */}
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: alpha(
                isEdit ? theme.palette.info.main : theme.palette.primary.main,
                0.12,
              ),
              color: isEdit
                ? theme.palette.info.main
                : theme.palette.primary.main,
              fontWeight: 600,
              fontSize: '0.9rem',
            }}
          >
            {isEdit ? initials : <UserPlus size={20} />}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} lineHeight={1.3}>
              {isEdit ? 'Modifier le membre' : 'Nouveau membre'}
            </Typography>
            {isEdit && (
              <Typography variant="body2" color="text.secondary">
                {row?.firstName} {row?.lastName}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          {/* Assignment */}
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={500}
              sx={{
                mb: 1.5,
                display: 'block',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Affectation
            </Typography>

            {/* Site select */}
            <SelectInput
              name="siteIds"
              options={siteOptions}
              errors={errors}
              control={control}
              SelectProps={{ multiple: true }}
            />
          </Box>

          {/* Identity */}
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={500}
              sx={{
                mb: 1.5,
                display: 'block',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Identité
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextInput name="firstName" errors={errors} control={control} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextInput name="lastName" errors={errors} control={control} />
              </Grid>
            </Grid>
          </Box>

          {/* Credentials */}
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={500}
              sx={{
                mb: 1.5,
                display: 'block',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Identifiants
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextInput
                  name="email"
                  errors={errors}
                  control={control}
                  inputProps={{
                    autocomplete: 'new-email',
                    form: { autocomplete: 'off' },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <PasswordInput
                  name="password"
                  control={control}
                  errors={errors}
                />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose} size="small">
          Annuler
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSubmit(onSubmit)}
          size="small"
        >
          {isEdit ? 'Enregistrer' : 'Ajouter'}
        </Button>
      </DialogActions>
    </>
  )
}
